import { useEffect, useState } from 'react';
import '../styles/Analytics.css';
import axios from 'axios';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { GridLoader } from 'react-spinners';

import { generateQRAnalyticsReport } from "./qrGenerationReport";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


function QRGeneration() {

  const [showPopupModal,      setShowPopupModal]      = useState(false);
  const [completeData,        setCompleteData]        = useState([]);
  const [skuData,             setSkuData]             = useState([]);
  const [productName,         setProductName]         = useState([]);
  const [selectedProduct,     setSelectedProduct]     = useState("all");
  const [dateRange,           setDateRange]           = useState("all");
  const [customYear,          setCustomYear]          = useState("");
  const [toDate,              setToDate]              = useState("");
  const [verLogs,             setVerLogs]             = useState(false);
  const [selectedQR,          setSelectedQR]          = useState(null);
  const [batchData,           setBatchData]           = useState([]);
  const [viewLevel,           setViewLevel]           = useState('year');
  const [selectedYear,        setSelectedYear]        = useState(null);
  const [selectedMonth,       setSelectedMonth]       = useState(null);
  const [selectedDate,        setSelectedDate]        = useState(null);
  const [breadcrumb,          setBreadcrumb]          = useState(['Year View']);
  const [chart2FormattedData, setChart2FormattedData] = useState([]);

  const baseURL = sessionStorage.getItem('baseURL');

  /* ── Helpers (logic unchanged) ────────────────────────────────────── */

  const generateBatchHistory = (meta) => {
    if (!meta.batch || !Array.isArray(meta.batch) || meta.batch.length === 0) return [];
    return meta.batch.map(b => ({
      batch_number:         b.batch_no,
      date_of_manufacturing: b.date_of_manufacturing,
      timestamp:            new Date(b.timestamp),
      quantity:             1,
    }));
  };

  const generateSkuColors = (data) => {
    const uniqueSkus = [...new Set(data.map(item => item.sku_id))].sort();
    const colors = {};
    uniqueSkus.forEach(sku => {
      let hex;
      do { hex = "#" + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, "0"); }
      while (hex === "#000000");
      colors[sku] = am4core.color(hex);
    });
    return colors;
  };

  const getMonthName = (month) =>
    ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month - 1];

  const handleBackNavigation = (level) => {
    if (level === 'year') {
      setViewLevel('year'); setSelectedYear(null); setSelectedMonth(null);
      setSelectedDate(null); setSelectedQR(null); setBatchData([]);
      setBreadcrumb(['Year View']);
    } else if (level === 'month') {
      setViewLevel('month'); setSelectedMonth(null); setSelectedDate(null);
      setSelectedQR(null); setBatchData([]);
      setBreadcrumb(['Year View', `${selectedYear}`]);
    } else if (level === 'date') {
      setViewLevel('date'); setSelectedDate(null); setSelectedQR(null); setBatchData([]);
      setBreadcrumb(['Year View', `${selectedYear}`, `${getMonthName(selectedMonth)}`]);
    } else if (level === 'qr') {
      setViewLevel('qr'); setSelectedQR(null); setBatchData([]);
      setBreadcrumb(['Year View', `${selectedYear}`, `${getMonthName(selectedMonth)}`, selectedDate]);
    }
  };

  const fetchChart2Details = () => {
    axios.get(baseURL + "qr/fetchQRGenerationAnalytics")
      .then(response => {
        setCompleteData(response.data);
        setChart2FormattedData(response.data);
      })
      .catch(err => console.error(err));
  };

  /* ── Chart builder (logic unchanged, imperative button removed) ───── */

  const callChart2 = () => {
    const formattedData = [...chart2FormattedData].sort(
      (a, b) => new Date(a.qr_creation_date) - new Date(b.qr_creation_date)
    );
    const skuColors = generateSkuColors(formattedData);

    am4core.useTheme(am4themes_animated);

    let chart = am4core.create("chartdiv2", am4charts.XYChart);
    chart.padding(20, 20, 20, 20);
    chart.scrollbarX = new am4core.Scrollbar();

    const title = chart.titles.create();
    title.fontSize    = 18;
    title.marginBottom = 15;

    let categoryAxis;
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;
    valueAxis.strictMinMax = false;
    valueAxis.renderer.labels.template.adapter.add("text", t =>
      Number.isInteger(Number(t)) ? t : ""
    );

    /* ── Year View ────────────────────────────────────────────────── */
    if (viewLevel === "year") {
      title.text = "QR Generated by Year";
      const yearMap = {};
      formattedData.forEach(d => {
        const y = new Date(d.qr_creation_date).getFullYear();
        yearMap[y] = (yearMap[y] || 0) + 1;
      });
      chart.data = Object.keys(yearMap).map(y => ({ category: y, value: yearMap[y] }));

      categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category   = "category";
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.title.text            = "Year";
      valueAxis.title.text               = "Number of QRs Generated";

      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.categoryX = "category";
      series.dataFields.valueY    = "value";
      series.columns.template.tooltipText = "[bold]{category}[/]\nQR Count: {valueY}";
      series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      series.columns.template.events.on("hit", ev => {
        const year = Number(ev.target.dataItem.categoryX);
        setSelectedYear(year);
        setViewLevel("month");
        setBreadcrumb(["Year View", String(year)]);
      });
    }

    /* ── Month View ───────────────────────────────────────────────── */
    else if (viewLevel === "month") {
      title.text = `QR Generated in ${selectedYear} by Month`;
      const monthMap = {};
      formattedData
        .filter(d => new Date(d.qr_creation_date).getFullYear() === selectedYear)
        .forEach(d => {
          const m = new Date(d.qr_creation_date).getMonth() + 1;
          monthMap[m] = (monthMap[m] || 0) + 1;
        });
      chart.data = Object.keys(monthMap).map(m => ({
        month: m, monthName: getMonthName(m), value: monthMap[m]
      }));

      categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "monthName";
      categoryAxis.title.text          = "Month";
      valueAxis.title.text             = "Number of QRs Generated";

      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.categoryX = "monthName";
      series.dataFields.valueY    = "value";
      series.columns.template.tooltipText = "[bold]{monthName}[/]\nQR Count: {valueY}";
      series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      series.columns.template.events.on("hit", ev => {
        const ctx = ev.target.dataItem.dataContext;
        setSelectedMonth(parseInt(ctx.month));
        setViewLevel("date");
        setBreadcrumb(prev => [...prev, ctx.monthName]);
      });
    }

    /* ── Date View ────────────────────────────────────────────────── */
    else if (viewLevel === "date") {
      title.text = `QR Generated in ${getMonthName(selectedMonth)} ${selectedYear}`;

      const grouped = {};
      formattedData
        .filter(d => {
          const dt = new Date(d.qr_creation_date);
          return dt.getFullYear() === selectedYear && dt.getMonth() + 1 === selectedMonth;
        })
        .forEach(d => {
          const date    = new Date(d.qr_creation_date);
          const hourKey = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 0, 0, 0).getTime();
          if (!grouped[hourKey]) grouped[hourKey] = { timestamp: hourKey, date: new Date(hourKey), qrs: [] };
          grouped[hourKey].qrs.push(d);
        });

      const sortedHours = Object.values(grouped).sort((a, b) => a.timestamp - b.timestamp);
      const allQRs = [];
      const qrMap  = {};
      sortedHours.forEach(h => {
        h.qrs.forEach(qr => {
          const key = `${qr.sku_id}__${qr.qr_id}`;
          if (!qrMap[key]) { qrMap[key] = qr; allQRs.push(qr); }
        });
      });

      chart.data = sortedHours.map(h => {
        const row = { date: h.date, qrs: h.qrs };
        allQRs.forEach(qr => { row[`${qr.sku_id}__${qr.qr_id}`] = 0; });
        h.qrs.forEach(qr  => { row[`${qr.sku_id}__${qr.qr_id}`] = 1; });
        return row;
      });

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.baseInterval           = { timeUnit: "hour", count: 1 };
      dateAxis.groupData              = false;
      dateAxis.renderer.minGridDistance = 80;
      dateAxis.title.text             = "Date";
      dateAxis.tooltip.disabled       = true;

      const dValueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      dValueAxis.min           = 0;
      dValueAxis.strictMinMax  = true;
      dValueAxis.title.text    = "Number of QRs Generated";
      dValueAxis.tooltip.disabled = true;
      dValueAxis.renderer.labels.template.adapter.add("text", t =>
        Number.isInteger(Number(t)) ? t : ""
      );

      chart.cursor           = new am4charts.XYCursor();
      chart.cursor.behavior  = "none";
      chart.cursor.snapToSeries = [];

      const skuSeriesMap = {};
      allQRs.forEach((qr) => {
        const fieldName = `${qr.sku_id}__${qr.qr_id}`;
        const series    = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.dateX  = "date";
        series.dataFields.valueY = fieldName;
        series.name    = qr.sku_id;
        series.stacked = true;
        series.fill    = am4core.color(skuColors[qr.sku_id]);
        series.stroke  = am4core.color("#000000");
        series.strokeOpacity = 0.3;
        series.strokeWidth   = 0.5;
        series.columns.template.width    = am4core.percent(100);
        series.columns.template.maxWidth = 180;
        series.columns.template.minWidth = 150;
        series.columns.template.tooltipText =
          `[bold]Product:[/] ${qr.sku_id}\n[bold]QR:[/] ${qr.qr_id}\n[bold]Time:[/] {dateX.formatDate("dd MMM yyyy HH:mm")}`;
        series.tooltip.pointerOrientation  = "vertical";
        series.tooltip.background.cornerRadius = 3;
        series.tooltip.background.fillOpacity  = 0.9;
        series.tooltip.getFillFromObject    = false;
        series.tooltip.background.fill      = am4core.color("#000000");
        series.tooltip.label.fill           = am4core.color("#FFFFFF");
        series.tooltip.label.padding(8, 12, 8, 12);
        series.tooltip.animationDuration    = 0;
        series.columns.template.adapter.add("tooltipText", (text, target) =>
          target.dataItem?.valueY === 0 ? undefined : text
        );
        series.columns.template.interactionsEnabled = true;
        series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
        series.columns.template.events.on("hit", (ev) => {
          const ctx      = ev.target.dataItem.dataContext;
          const clickedQR = ctx.qrs.find(q => q.qr_id === qr.qr_id);
          if (!clickedQR) return;
          setSelectedQR(clickedQR);
          setBatchData(generateBatchHistory(clickedQR));
          setViewLevel("batch");
          setBreadcrumb(prev => [...prev, clickedQR.qr_id]);
        });
        if (!skuSeriesMap[qr.sku_id]) {
          skuSeriesMap[qr.sku_id]  = series;
          series.hiddenInLegend    = false;
        } else {
          series.hiddenInLegend = true;
        }
      });

      Object.keys(skuSeriesMap).forEach(sku => {
        const leg = skuSeriesMap[sku];
        leg.events.on("hidden", () => { chart.series.each(s => { if (s.name === sku && s !== leg) s.hide(); }); });
        leg.events.on("shown",  () => { chart.series.each(s => { if (s.name === sku && s !== leg) s.show(); }); });
      });

      chart.legend           = new am4charts.Legend();
      chart.legend.position  = "right";
      chart.legend.marginLeft = 20;
      chart.legend.useDefaultMarker = true;
      chart.legend.scrollable = true;
    }

    /* ── Batch View ───────────────────────────────────────────────── */
    else if (viewLevel === "batch") {
      title.text = `Batch History for ${selectedQR.qr_id}`;
      chart.series.clear();
      chart.xAxes.clear();
      chart.yAxes.clear();

      if (batchData.length === 0) {
        const emptyMessage   = chart.createChild(am4core.Label);
        emptyMessage.text    = "No batch history available for this QR";
        emptyMessage.fontSize = 16;
        emptyMessage.fontWeight = "500";
        emptyMessage.fill    = am4core.color("#000");
        chart.data = [];
        return;
      }

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.groupIntervals.setAll([
        { timeUnit: "minute", count: 1 },
        { timeUnit: "minute", count: 5 },
        { timeUnit: "hour",   count: 1 },
        { timeUnit: "day",    count: 1 },
      ]);
      dateAxis.title.text = "Date";

      const bValueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      bValueAxis.title.text = "Number of Batches Created";
      bValueAxis.min = 0;
      bValueAxis.renderer.labels.template.adapter.add("text", t =>
        Number.isInteger(Number(t)) ? t : ""
      );

      const chartData   = batchData.map(b => ({ ...b, date: new Date(b.timestamp) }));
      chart.data        = chartData;
      const batchGroups = {};
      chartData.forEach(b => {
        if (!batchGroups[b.batch_number]) batchGroups[b.batch_number] = [];
        batchGroups[b.batch_number].push(b);
      });
      Object.keys(batchGroups).forEach(batchNo => {
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.dateX  = "date";
        series.dataFields.valueY = "quantity";
        series.name = batchNo;
        series.data = batchGroups[batchNo];
        series.columns.template.tooltipText =
          "[bold]Batch: {batch_number}[/]\nDate of Manufacture: {date_of_manufacturing}";
      });
    }

    // ── NO imperative DOM button here — see React-rendered button below ──

    return () => { chart.dispose(); };
  };

  /* ── Effects ──────────────────────────────────────────────────────── */

  useEffect(() => {
    callChart2();
  }, [viewLevel, selectedYear, selectedMonth, selectedQR, batchData, chart2FormattedData]);

  useEffect(() => {
    handleBackNavigation('year');
    fetchChart2Details();
    document.getElementById('loading').hidden = true;
  }, []);

  useEffect(() => {
    const prods = [];
    completeData.forEach(item => {
      if (item.sku_id && !prods.includes(item.sku_id)) prods.push(item.sku_id);
    });
    setProductName(prods);
  }, [completeData]);

  /* ── Date range helper ────────────────────────────────────────────── */

  function getDateRange() {
    const now = new Date();
    let start = null, end = null;
    switch (dateRange) {
      case "year":
        start = new Date(now.getFullYear(), 0, 1); end = new Date(); break;
      case "lastYear":
        start = new Date(now.getFullYear() - 1, 0, 1);
        end   = new Date(now.getFullYear() - 1, 11, 31); break;
      case "custom":
        start = new Date(customYear, 0, 1);
        end   = new Date(customYear, 11, 31);
        end.setHours(23); end.setMinutes(59); end.setSeconds(59); break;
      default: break;
    }
    return { start, end };
  }

  const triggerGeneration = () => {
    if (dateRange === "custom" && !customYear) {
      alert("Please select the custom Year."); return;
    }
    const { start, end } = getDateRange();
    const filtered = completeData.filter(item => {
      const itemDate     = new Date(item.qr_creation_date);
      const productMatch = !selectedProduct || selectedProduct === "all"
        ? true : item.sku_id === selectedProduct;
      const dateMatch    = !start || !end ? true : itemDate >= start && itemDate <= end;
      return productMatch && dateMatch;
    });
    if (dateRange === "all") setVerLogs(false);
    setShowPopupModal(false);
    document.getElementById('loading').hidden = false;
    generateQRAnalyticsReport(filtered, selectedProduct, start, verLogs);
    setSelectedProduct('all');
    setDateRange('all');
    setVerLogs(false);
    document.getElementById('loading').hidden = true;
  };

  /* ── Breadcrumb label helper ──────────────────────────────────────── */
  const breadcrumbLabel = (item, idx) => {
    if (idx === 0) return "All Data";
    if (idx === 1) return `Year: ${item}`;
    if (idx === 2) return `Month: ${item}`;
    return `QR: ${item}`;
  };

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <>
      {/* ── Breadcrumb + QR details ──────────────────────────────── */}
      <div className="qr-toolbar">
        <div className="qr-breadcrumb">
          {breadcrumb.map((item, idx) => (
            <button
              key={idx}
              disabled={idx === breadcrumb.length - 1}
              className={`breadcrumb-btn ${idx === breadcrumb.length - 1 ? 'active' : ''}`}
              onClick={() => {
                if (idx === 0) handleBackNavigation("year");
                else if (idx === 1) handleBackNavigation("month");
                else if (idx === 2) handleBackNavigation("date");
                else if (idx === 3) handleBackNavigation("qr");
              }}
            >
              {breadcrumbLabel(item, idx)}
            </button>
          ))}
        </div>

        {/* View Statistics — React-rendered, Cadila-styled */}
        <button className="chart-action-btn" onClick={() => setShowPopupModal(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          View Statistics
        </button>
      </div>

      {/* ── Selected QR details strip ─────────────────────────────── */}
      {selectedQR && viewLevel === "batch" && (
        <div className="qr-detail-strip">
          <div className="qr-detail-item">
            <span className="qr-detail-label">Product Name</span>
            <span className="qr-detail-value">{selectedQR.sku_id}</span>
          </div>
          <div className="qr-detail-item">
            <span className="qr-detail-label">Creation Date</span>
            <span className="qr-detail-value">
              {new Date(selectedQR.qr_creation_date).toLocaleString('en-IN', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true
              })}
            </span>
          </div>
        </div>
      )}

      <div className="chart-container">
        <div id="chartdiv2" style={{ width: "100%", height: "60vh" }} />
      </div>

      {/* ── Report popup ──────────────────────────────────────────── */}
      {showPopupModal && (
        <div className="popup-modal">
          <div className="popup-content">
            <h3>Generate Report</h3>
            <div className="popup-filter">

              <div className="popup-filter-item">
                <label>Product Name</label>
                <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
                  <option value="all">All Products</option>
                  {productName.map((product, index) => (
                    <option key={index} value={product}>{product}</option>
                  ))}
                </select>
              </div>

              <div className="popup-filter-item">
                <label>Date Range</label>
                <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="year">Current Year</option>
                  <option value="lastYear">Last Year</option>
                  <option value="custom">Custom Year</option>
                </select>
              </div>

              {dateRange === "custom" && (
                <div className="popup-filter-item">
                  <label>Custom Year</label>
                  <DatePicker
                    selected={customYear}
                    onChange={(date) => setCustomYear(date)}
                    showYearPicker
                    dateFormat="yyyy"
                    className="custom-year-input"
                    minDate={new Date("1900-01-01")}
                    maxDate={new Date()}
                  />
                </div>
              )}

              {dateRange !== "all" && (
                <div className="popup-filter-item check">
                  <input
                    type="checkbox"
                    id="verLogsCheckQR"
                    onChange={(e) => setVerLogs(e.target.checked)}
                  />
                  <span onClick={() => setVerLogs(v => !v)}>Show Detailed QR-Batch Logs</span>
                </div>
              )}
            </div>

            <div className="popup-buttons">
              <button className="popup-button can" onClick={() => setShowPopupModal(false)}>Cancel</button>
              <button className="popup-button gen" onClick={triggerGeneration}>Generate Report</button>
            </div>
          </div>
        </div>
      )}

      <div className="loading" id="loading">
        <GridLoader color="#2C3493" loading={true} size={20} />
      </div>
    </>
  );
}

export default QRGeneration;